import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/use-toast';
import { searchLostArkCharacter, updateCharacters, checkCharacterNicknameDuplicate, getMyCharacters, type LostArkCharacter, type CharacterRegistration, type Character } from '@/api/characterApi';
import { Search, Loader2, AlertTriangle, ChevronDown, ChevronUp, Check, X } from 'lucide-react';
import ClassIconSimple from '@/components/ClassIconSimple';
import { preloadClassIcons } from '@/utils/imagePreload';

type CharacterSelection = {
  character: LostArkCharacter;
  role: 'supporter' | 'attacker';
};

export default function CharacterRegisterPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [characterName, setCharacterName] = useState('');
  const [searching, setSearching] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [characters, setCharacters] = useState<LostArkCharacter[]>([]);
  const [selectedCharacters, setSelectedCharacters] = useState<Map<string, CharacterSelection>>(new Map());
  const [showSelectedList, setShowSelectedList] = useState(false);
  const [myCharacters, setMyCharacters] = useState<Character[]>([]);
  const [loadingMyCharacters, setLoadingMyCharacters] = useState(true);

  // 페이지 로드 시 내 캐릭터 불러오기
  useEffect(() => {
    const loadMyCharacters = async () => {
      setLoadingMyCharacters(true);
      try {
        const chars = await getMyCharacters();
        setMyCharacters(chars);

        // Character를 LostArkCharacter 형식으로 변환하여 선택된 상태로 추가
        const initialSelected = new Map<string, CharacterSelection>();
        chars.forEach((char) => {
          const lostArkChar: LostArkCharacter = {
            ServerName: '', // 서버 정보는 없음
            CharacterName: char.characterNickname,
            CharacterLevel: 0, // 캐릭터 레벨 정보 없음
            CharacterClassName: char.characterClass,
            ItemAvgLevel: char.itemLevel,
            ItemMaxLevel: char.itemLevel,
          };
          initialSelected.set(char.characterNickname, {
            character: lostArkChar,
            role: char.classType as 'supporter' | 'attacker',
          });
        });
        setSelectedCharacters(initialSelected);
        if (initialSelected.size > 0) {
          setShowSelectedList(true);
        }

        // 내 캐릭터의 클래스 아이콘 프리로드
        const myClassNames = chars.map(char => char.characterClass);
        preloadClassIcons(myClassNames);
      } catch (error) {
        console.error('내 캐릭터 로드 실패:', error);
      } finally {
        setLoadingMyCharacters(false);
      }
    };

    loadMyCharacters();
  }, []);

  // 검색 결과 클래스 아이콘 프리로드
  useEffect(() => {
    if (characters.length === 0) return;

    const classNames = characters.map(char => char.CharacterClassName);
    preloadClassIcons(classNames);
  }, [characters]);

  // 서폿 직업 판별
  const isSupporter = (className: string): boolean => {
    return ['바드', '도화가'].includes(className);
  };

  // 역할 전환 가능한 직업 (홀리나이트, 발키리)
  const canToggleRole = (className: string): boolean => {
    return ['홀리나이트', '발키리'].includes(className);
  };

  // 직업에 따른 기본 역할 결정
  const getDefaultRole = (className: string): 'supporter' | 'attacker' => {
    if (isSupporter(className) || canToggleRole(className)) {
      return 'supporter';
    }
    return 'attacker';
  };

  // 역할 전환 시 직업명 변경 (홀리나이트 → 심판자, 발키리 → 신의기사)
  const getRoleDisplayName = (className: string, role: 'supporter' | 'attacker'): string => {
    if (role === 'attacker') {
      if (className === '홀리나이트') return '심판자';
      if (className === '발키리') return '신의기사';
    }
    return className;
  };

  // Lost Ark API로 캐릭터 검색
  const handleSearch = async () => {
    if (!characterName.trim()) {
      toast({
        title: '캐릭터 이름을 입력해주세요.',
        variant: 'destructive',
      });
      return;
    }

    setSearching(true);

    try {
      // 1. 내 캐릭터인지 확인
      const isMyCharacter = myCharacters.some(
        (char) => char.characterNickname.toLowerCase() === characterName.trim().toLowerCase()
      );

      // 2. 내 캐릭터가 아닌 경우에만 중복 체크
      if (!isMyCharacter) {
        const isAvailable = await checkCharacterNicknameDuplicate(characterName.trim());

        if (!isAvailable) {
          toast({
            title: '이미 등록된 캐릭터입니다.',
            description: '다른 사용자가 등록한 캐릭터는 검색할 수 없습니다.',
            variant: 'destructive',
          });
          setCharacters([]);
          setSearching(false);
          return;
        }
      }

      // 2. Lost Ark API로 검색
      const result = await searchLostArkCharacter(characterName.trim());

      if (result.length === 0) {
        toast({
          title: '캐릭터를 찾을 수 없습니다.',
          description: '캐릭터 이름을 확인해주세요.',
          variant: 'destructive',
        });
        setCharacters([]);
      } else {
        // 3. 1640 미만 필터링
        const filteredCharacters = result.filter((character) => {
          const itemLevel = parseFloat(character.ItemAvgLevel.replace(',', ''));
          return itemLevel >= 1640;
        });

        if (filteredCharacters.length === 0) {
          toast({
            title: '1640 이상 캐릭터가 없습니다.',
            description: '아이템 레벨 1640 이상의 캐릭터만 등록할 수 있습니다.',
            variant: 'destructive',
          });
          setCharacters([]);
          return;
        }

        // 4. 레벨순으로 정렬 (높은 레벨부터)
        const sortedCharacters = filteredCharacters.sort((a, b) => {
          const levelA = parseFloat(a.ItemAvgLevel.replace(',', ''));
          const levelB = parseFloat(b.ItemAvgLevel.replace(',', ''));
          return levelB - levelA;
        });

        setCharacters(sortedCharacters);
        toast({
          title: `${sortedCharacters.length}개의 캐릭터를 찾았습니다.`,
          description: '레이드에 참여할 캐릭터를 선택해주세요.',
        });
      }
    } catch (error: any) {
      console.error('캐릭터 검색 실패:', error);

      if (error.response?.status === 503) {
        toast({
          title: '로스트아크 서버 점검 중',
          description: '로스트아크 서버가 점검 중입니다. 나중에 다시 시도해주세요.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: '캐릭터 검색 실패',
          description: error.response?.data?.message || '캐릭터 검색 중 오류가 발생했습니다.',
          variant: 'destructive',
        });
      }
      setCharacters([]);
    } finally {
      setSearching(false);
    }
  };

  // 캐릭터 선택/해제/역할 전환
  const handleCharacterClick = (character: LostArkCharacter) => {
    const key = character.CharacterName;
    const newSelected = new Map(selectedCharacters);

    if (newSelected.has(key)) {
      const current = newSelected.get(key)!;

      // 역할 전환 가능한 직업이고 현재 서폿이면 딜러로 전환
      if (canToggleRole(character.CharacterClassName) && current.role === 'supporter') {
        newSelected.set(key, { character, role: 'attacker' });
        toast({
          title: '역할 전환',
          description: `${character.CharacterName}을(를) 딜러로 변경했습니다.`,
        });
      } else {
        // 그 외의 경우 선택 해제
        newSelected.delete(key);
      }
    } else {
      // 새로 선택
      if (newSelected.size >= 15) {
        toast({
          title: '최대 15개까지 선택 가능합니다.',
          variant: 'destructive',
        });
        return;
      }
      newSelected.set(key, { character, role: getDefaultRole(character.CharacterClassName) });
    }

    setSelectedCharacters(newSelected);
  };

  // 캐릭터 등록/재등록
  const handleSubmit = async () => {
    if (selectedCharacters.size === 0) {
      toast({
        title: '캐릭터를 선택해주세요.',
        variant: 'destructive',
      });
      return;
    }

    setRegistering(true);

    try {
      const charactersToRegister: CharacterRegistration[] = Array.from(selectedCharacters.values()).map(({ character, role }) => ({
        characterClass: getRoleDisplayName(character.CharacterClassName, role),
        characterNickname: character.CharacterName,
        itemLevel: character.ItemAvgLevel.replace(',', ''),
        classType: role,
      }));

      const response = await updateCharacters(charactersToRegister)

      if (response.success) {
        toast({
          title: '캐릭터 등록 성공!',
          description: response.message,
        });

        // 스케줄 페이지로 이동
        navigate('/schedule');
      } else {
        toast({
          title: '캐릭터 등록 실패',
          description: response.message,
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('캐릭터 등록 실패:', error);
      toast({
        title: '오류',
        description: error.response?.data?.message || '캐릭터 등록 중 오류가 발생했습니다.',
        variant: 'destructive',
      });
    } finally {
      setRegistering(false);
    }
  };

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">
              {'캐릭터 등록'}
            </CardTitle>
            <CardDescription>
              {'로스트아크 캐릭터를 검색하여 등록해주세요'}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* 로딩 중 */}
            {loadingMyCharacters && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                <span className="ml-3 text-muted-foreground">캐릭터 정보 불러오는 중...</span>
              </div>
            )}

            {/* 업데이트 모드 안내 */}
            {!loadingMyCharacters && myCharacters.length > 0 && (
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900/50 rounded-lg p-4 flex gap-3">
                <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-300">안내</p>
                  <p className="text-sm text-blue-700 dark:text-blue-400">
                    기존 등록된 {myCharacters.length}개의 캐릭터가 자동으로 선택되었습니다.
                    추가 캐릭터를 검색하거나, 불필요한 캐릭터를 클릭하여 선택 해제할 수 있습니다.
                  </p>
                </div>
              </div>
            )}

            {/* 검색 영역 */}
            {!loadingMyCharacters && (
              <div className="space-y-2">
                <Label htmlFor="characterName">캐릭터 이름</Label>
                <div className="flex gap-2">
                  <Input
                    id="characterName"
                    type="text"
                    placeholder="캐릭터 이름을 입력하세요"
                    value={characterName}
                    onChange={(e) => setCharacterName(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    disabled={searching}
                  />
                  <Button onClick={handleSearch} disabled={searching || !characterName.trim()}>
                    {searching ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        검색 중...
                      </>
                    ) : (
                      <>
                        <Search className="mr-2 h-4 w-4" />
                        검색
                      </>
                    )}
                  </Button>
                </div>
                <div className="space-y-1 text-sm text-muted-foreground">
                  <p>• 원정대 캐릭터 중 하나의 이름을 입력하면 모든 캐릭터가 표시됩니다.</p>
                  <p>• 홀리나이트, 발키리는 한 번 더 클릭 시 딜러로 전환됩니다.</p>
                  <p>• 부계정 추가 시 재검색 후 추가하세요. (최대 15개)</p>
                </div>
              </div>
            )}

            {/* 선택된 캐릭터 목록 보기 */}
            {selectedCharacters.size > 0 && (
              <div className="bg-gradient-to-br from-accent/20 to-accent/30 border border-accent rounded-lg p-4">
                <button
                  onClick={() => setShowSelectedList(!showSelectedList)}
                  className="w-full flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm">
                      {selectedCharacters.size}
                    </div>
                    <span className="font-semibold">
                      선택된 캐릭터
                    </span>
                  </div>
                  {showSelectedList ? (
                    <ChevronUp className="w-5 h-5 text-primary" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-primary" />
                  )}
                </button>

                {showSelectedList && (
                  <div className="space-y-2 mt-3 pt-3 border-t border-accent">
                    {Array.from(selectedCharacters.values()).map(({ character, role }) => (
                      <div
                        key={character.CharacterName}
                        className="flex items-center justify-between p-3 bg-card rounded-lg border shadow-sm hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <ClassIconSimple
                            characterClass={getRoleDisplayName(character.CharacterClassName, role)}
                            classType={role}
                            size={32}
                          />
                          <div className="flex flex-col flex-1 min-w-0">
                            <span className="font-semibold text-sm text-foreground truncate">
                              {character.CharacterName}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {getRoleDisplayName(character.CharacterClassName, role)}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="font-mono text-sm font-medium text-foreground">
                            {character.ItemAvgLevel}
                          </span>
                          {role === 'supporter' ? (
                            <Badge className="bg-green-500 hover:bg-green-600 text-xs">서폿</Badge>
                          ) : canToggleRole(character.CharacterClassName) ? (
                            <Badge variant="destructive" className="text-xs">딜러</Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs">딜러</Badge>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 hover:bg-destructive/10 hover:text-destructive"
                            onClick={() => {
                              const newSelected = new Map(selectedCharacters);
                              newSelected.delete(character.CharacterName);
                              setSelectedCharacters(newSelected);
                              toast({
                                title: '캐릭터 제거',
                                description: `${character.CharacterName}을(를) 목록에서 제거했습니다.`,
                              });
                            }}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* 검색 결과 */}
            {characters.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">
                  검색 결과 ({characters.length}개)
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {characters.map((character) => {
                    const selection = selectedCharacters.get(character.CharacterName);
                    const isSelected = !!selection;
                    const currentRole = selection?.role || getDefaultRole(character.CharacterClassName);
                    const canToggle = canToggleRole(character.CharacterClassName);

                    return (
                      <div
                        key={character.CharacterName}
                        onClick={() => handleCharacterClick(character)}
                        className={`
                          flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all
                          ${isSelected
                            ? 'border-primary bg-primary/5'
                            : 'border-gray-200 hover:border-gray-300'
                          }
                        `}
                      >
                        <div
                          className={`
                            w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0
                            ${isSelected ? 'border-primary bg-primary' : 'border-gray-300'}
                          `}
                        >
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>

                        <ClassIconSimple
                          characterClass={isSelected ? getRoleDisplayName(character.CharacterClassName, currentRole) : character.CharacterClassName}
                          classType={currentRole}
                          size={36}
                        />

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-semibold text-sm truncate">
                              {character.CharacterName}
                            </p>
                            {isSelected && currentRole === 'supporter' && (
                              <Badge variant="secondary" className="text-xs">서폿</Badge>
                            )}
                            {isSelected && currentRole === 'attacker' && canToggle && (
                              <Badge variant="outline" className="text-xs">딜러</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span>
                              {isSelected
                                ? getRoleDisplayName(character.CharacterClassName, currentRole)
                                : character.CharacterClassName}
                            </span>
                            <span>•</span>
                            <span className="font-mono">Lv.{character.ItemAvgLevel}</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {character.ServerName}
                          </p>
                          {canToggle && (
                            <p className="text-xs text-blue-600 mt-1">
                              한 번 더 클릭 시 {currentRole === 'supporter' ? '딜러' : '서폿'}로 전환
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 검색 전 안내 */}
            {characters.length === 0 && !searching && (
              <div className="text-center py-12 text-muted-foreground">
                <Search className="w-12 h-12 mx-auto mb-4 opacity-20" />
                <p className="mb-2">캐릭터 이름을 입력하고 검색 버튼을 클릭하세요</p>
                <p className="text-sm">(아이템 레벨 1,640 미만의 캐릭터는 노출되지 않습니다)</p>
              </div>
            )}

            {/* 등록/업데이트 버튼 (항상 표시) */}
            {!loadingMyCharacters && (
              <div className="flex items-center justify-between pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  {selectedCharacters.size}개 선택됨
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => navigate('/schedule')}>
                    취소
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={registering || selectedCharacters.size === 0}
                  >
                    {registering ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {'등록 중...'}
                      </>
                    ) : (
                      <>{'등록하기'}</>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
